import { useMemo } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { css } from '@emotion/react';
import { BookOpenText } from '@phosphor-icons/react';
import { getPlaceholderUrl } from 'common/commonUtil';
import { useEditAppContext } from 'client/lib/EditApp';
import { useUser } from 'client/lib/User';
import { useDeploymentClientState } from 'client/state/DeploymentClientState';
import { useDeploymentStatusTree } from 'client/state/useDeploymentStatusTree';
import { getDeploymentStageIcon } from 'client/ClientConstants';
import type { BreadcrumbsItemProps } from 'client/components/Breadcrumbs';
import { Button } from 'client/components/Button';
import { Skeleton } from 'client/components/Skeleton';
import { getFeaturesFromState } from 'client/components/Status/statusUtils';
import { Subheader } from 'client/components/Subheader';
import { AddTimeButton } from './components/AddTimeButton';
import { DeploymentStatusTag } from './components/DeploymentStatusTag';
import { EnabledToggleButton } from './components/EnabledToggleButton';
import { ScheduleStatus } from './components/ScheduleStatus';
import { ViewAppButton } from './components/ViewApp/ViewAppButton';

export const DeploymentSubheader = () => {
  const params = useParams();
  const { pathname } = useLocation();
  const { user } = useUser();
  const deploymentID = params.deploymentID ? parseInt(params.deploymentID) : 0;

  const projectID = parseInt(params.projectID || '0');
  const project = user?.projects.find((project) => project.id === projectID);
  const projectName = project?.name || '';

  //const { podTaskStateID, identifier: actionIdentifier, run: runAction } = useAction(deploymentID);
  const [deploymentName, stageIdentifier] = useDeploymentClientState((state) => [state.document?.name, state.document?.stage || '']);
  // const templateActions = useDeploymentClientState((state) => state.templateConfiguration?.actions || []);
  const isQuickLaunch = useDeploymentClientState((state) => state.document?.repositoryUrl == null);
  const stageIcon = getDeploymentStageIcon(stageIdentifier);
  const statusTree = useDeploymentStatusTree(deploymentID);
  const combinedState = {
    deploymentStateID: statusTree?.deploymentStateID,
    coreStateID: statusTree?.core.coreStateID,
    itemStateID: statusTree?.itemStateID,
  };
  // const stateFeatures = getFeaturesFromState(combinedState);
  const editAppContext = useEditAppContext();

  const activeSection = (pathname.split('/').length === 7 && pathname.split('/').pop()) || 'overview';

  // const actionItems = useMemo<MenuItemProps[]>(
  //   () =>
  //     templateActions.map((action) => ({
  //       id: action.identifier,
  //       title: action.name,
  //       subtitle: action.command,
  //       icon:
  //         actionIdentifier === action.identifier && podTaskStateID !== null ? (
  //           <Status
  //             itemStateID={podTaskStateToStatusState[podTaskStateID]}
  //             isComplete={podTaskStateID !== PodTaskStateID.PENDING}
  //             isPending={podTaskStateID === PodTaskStateID.PENDING}
  //             noBlink
  //           />
  //         ) : (
  //           <TerminalWindow />
  //         ),
  //       onSelect: () => runAction(action.identifier),
  //     })),
  //   [actionIdentifier, podTaskStateID, runAction, templateActions]
  // );

  const breadcrumbs = useMemo<BreadcrumbsItemProps[]>(
    () => [
      {
        title: 'Projects',
        href: '../../projects',
      },
      {
        title: projectName || '',
        image: {
          alt: 'project image',
          src: getPlaceholderUrl('project', projectID),
          shape: 'hexagon',
        },
        href: '../',
      },
      {
        title: deploymentName || '',
        icon: <stageIcon.type weight="duotone" />,
        disabled: true,
      },
    ],
    [projectName, projectID, deploymentName, stageIcon]
  );

  const tabsProps = useMemo(
    () => ({
      items: [
        { id: 'overview', title: 'Overview', href: '.' },
        {
          id: 'repository',
          title: 'Repository & Git',
          href: 'repository',
        },
        {
          id: 'options',
          title: 'Options',
          href: 'options',
          disabled: false,
        },
      ],
      selected: activeSection,
    }),
    [activeSection]
  );

  const isLoading = !editAppContext.isReady;

  let title: JSX.Element | string = <Skeleton width="60%" />;
  if (!isLoading) title = deploymentName || ' ';

  const icon = isLoading ? (
    <Skeleton width={40} height={40} style={{ borderRadius: '50%', transform: 'scale(0.9)' }} />
  ) : (
    <stageIcon.type weight="duotone" />
  );
  const state = isLoading || !statusTree ? 'disabled' : combinedState;

  return (
    <>
      <Subheader
        title={title}
        statusBar
        state={state}
        icon={icon}
        type="Deployment"
        subtitle={
          <span css={statusStyle}>
            <DeploymentStatusTag statusTree={statusTree} />
            <ScheduleStatus />
          </span>
        }
        breadcrumbs={breadcrumbs}
        actions={
          <>
            <Button
              icon={<BookOpenText weight="bold" />}
              tooltip="Documentation"
              tooltipSide="left"
              href="https://docs.diploi.com/concepts/deployment"
              external
            />
            {/* <MenuButton
              id="deployment.actions"
              icon={<CaretCircleDown weight="bold" />}
              items={actionItems}
              primary
              disabled={!stateFeatures.isEnabled || !stateFeatures.isToggleable}
              menuProps={{
                anchorOrigin: {
                  vertical: 'bottom',
                  horizontal: 'right',
                },
                transformOrigin: {
                  vertical: 'top',
                  horizontal: 'right',
                },
              }}
            >
              Actions
            </MenuButton> */}
            <AddTimeButton deploymentID={deploymentID} />
            <EnabledToggleButton deploymentID={deploymentID} stageIdentifier={stageIdentifier} />
            <ViewAppButton />
          </>
        }
        tabs={tabsProps}
      />
    </>
  );
};

const quickLaunchIconStyle = css`
  position: absolute;
  fill: url(#gradient-one);
  transform: translateY(-3px);
`;

const statusStyle = css`
  display: flex;
  align-items: center;
  gap: 8px;

  > label > svg {
    transform: translateY(0.5px);
  }
`;
